"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useCallback, useRef } from "react";
const STORAGE_KEY = "cova:fab-drag-tooltip-seen";
const SHOW_DELAY = 1500;
const AUTO_DISMISS_DELAY = 3500;
function hasSeenTooltip(): boolean {
  if (typeof window === "undefined") return true;
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return true;
  }
}
function markTooltipSeen() {
  try {
    localStorage.setItem(STORAGE_KEY, "true");
  } catch {
    // localStorage unavailable
  }
}

/**
 * One-time "Drag to reposition" tooltip for the FAB group.
 * Shows on first visit after a short delay, auto-dismisses, and never shows again.
 */
export function useDragTooltip(isDragEnabled) {
  const $ = _c(6);
  const [showTooltip, setShowTooltip] = useState(false);
  const showTimer = useRef(null);
  const dismissTimer = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setShowTooltip(false);
      markTooltipSeen();
      if (showTimer.current) {
        clearTimeout(showTimer.current);
      }
      if (dismissTimer.current) {
        clearTimeout(dismissTimer.current);
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const dismissTooltip = t0;
  let t1;
  let t2;
  if ($[1] !== isDragEnabled) {
    t1 = () => {
      if (!isDragEnabled || hasSeenTooltip()) {
        return;
      }
      showTimer.current = setTimeout(() => {
        setShowTooltip(true);
        dismissTimer.current = setTimeout(() => {
          setShowTooltip(false);
          markTooltipSeen();
        }, AUTO_DISMISS_DELAY);
      }, SHOW_DELAY);
      return () => {
        if (showTimer.current) {
          clearTimeout(showTimer.current);
        }
        if (dismissTimer.current) {
          clearTimeout(dismissTimer.current);
        }
      };
    };
    t2 = [isDragEnabled];
    $[1] = isDragEnabled;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== showTooltip) {
    t3 = {
      showTooltip,
      dismissTooltip
    };
    $[4] = showTooltip;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}